import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { Button, Icon, Spinner, TuteroLogo } from '@tutero/ui';
import { ShimmerLines } from '@/lib/shimmer';

import {
  ACCOUNT_KEY,
  accountQuery,
  enrolTextFactor,
  phoneNumberOf,
  resendVerificationEmail,
  sendPhoneCode,
  verifyPhoneNumber,
} from '@/lib/account';
import { isStaff, signOut, signUpWithEmail, toAuthError } from '@/lib/auth';

import { useQuietShell } from '../quiet';
import { useRefreshSession, useSession } from '../session';
import { AuthErrorText, AuthField } from './AuthField';
import {
  fileAdminAccessRequest,
  readAdminAccessRequest,
  said,
} from './requestAccess.api';

/** The address Flutter's own request screen has (`admin_app_layout.dart:123-126`). */
export const REQUEST_ACCESS_PATH = '/admin-access';

/** The caller's own request, which is the only fact the fourth face is drawn from (D3). */
const REQUEST_KEY = ['admin-access-request'] as const;

const requestQuery = queryOptions({
  queryKey: REQUEST_KEY,
  queryFn: readAdminAccessRequest,
});

/**
 * Request Access (`request-access-is-a-page` D1, D2): the one surface in the admin that
 * creates an account, at a route of the shell's own, reached while nobody is signed in and
 * while a session holds no staff role — a requester is exactly a signed-in non-staff user,
 * so `Root` answers this address before either refusal.
 *
 * Four faces, as Flutter has (`admin_access_form.dart:32-63`), but chosen from the session
 * and the account the Worker answers with rather than from local booleans — and the fourth
 * only from the server's own word that a request exists (D3). Flutter computes "received"
 * from `emailVerified && enrolledFactors.isNotEmpty`, so a failed write leaves a stranger
 * told their request landed when no record exists anywhere (F3). Here the record is filed
 * the moment the account exists, and nothing but the self-read can say so.
 *
 * Two more faces than Flutter has, because the row now carries an outcome: a rejected
 * requester is told so rather than left reading *we will get back to you soon* forever, and
 * an approved one whose session has not caught up is told they are in. Both come off
 * `status`, which is the server's word too.
 *
 * The typed password lives in this component's state for the length of the flow and nowhere
 * else — never `sessionStorage`, never `localStorage` — because `enrolTextFactor` needs it
 * again (D5).
 */
export function RequestAccessPage() {
  /* A cluster change must not reload a typed password or a pending code mid-flow (D2). */
  useQuietShell();
  const [password, setPassword] = useState('');
  const session = useSession();
  const staff = session.data ?? null;
  const asking = staff !== null && !isStaff(staff.role);
  const account = useQuery({ ...accountQuery, enabled: asking });
  const request = useQuery({ ...requestQuery, enabled: asking });

  /* One face, in this order; only the last two are terminal, so only they drop the steps. */
  function face(): { step: Step | null; body: React.ReactNode } {
    if (session.isPending) {
      return {
        step: null,
        body: (
          <div
            role="status"
            aria-label="Reading your account"
            className="w-full"
          >
            <ShimmerLines count={3} />
          </div>
        ),
      };
    }
    if (session.isError) {
      return {
        step: null,
        body: (
          <Refused title="Your account could not be read">
            Reload the page to try again.
          </Refused>
        ),
      };
    }
    if (staff === null) {
      return { step: 'account', body: <SignUpFace onAsked={setPassword} /> };
    }
    if (isStaff(staff.role)) {
      return { step: null, body: <AlreadyStaffFace email={staff.email} /> };
    }
    /* Both outcomes are terminal and both come before the steps: no mail and no texted code
       changes an answer that is already recorded, so neither is asked for again. */
    if (request.data?.status === 'rejected') {
      return { step: null, body: <RejectedFace /> };
    }
    if (request.data?.status === 'approved') {
      return { step: null, body: <ApprovedFace /> };
    }
    if (account.isPending) {
      return {
        step: 'verification',
        body: (
          <div
            role="status"
            aria-label="Reading your account"
            className="w-full"
          >
            <ShimmerLines count={3} />
          </div>
        ),
      };
    }
    if (account.isError || account.data === undefined) {
      return {
        step: null,
        body: (
          <Refused
            title="Your account could not be read"
            action={<Retry onPress={() => void account.refetch()} />}
          >
            The sign-in service did not answer. Nothing about your request has
            changed.
          </Refused>
        ),
      };
    }
    /* A read that errored says nothing, and nothing is not a request (D3). */
    const filed = request.data?.adminAccessRequestedAt != null;
    if (!account.data.emailVerified) {
      return {
        step: 'account',
        body: <ConfirmEmailFace email={account.data.email} filed={filed} />,
      };
    }
    if (!account.data.twoFactorEnabled) {
      return {
        step: 'verification',
        body: <VerifyPhoneFace password={password} filed={filed} />,
      };
    }
    return {
      step: null,
      body: filed ? <ReceivedFace /> : <NotFiledFace />,
    };
  }

  const { step, body } = face();
  return (
    /* 640px and a card, as Flutter's screen is (`admin_access_screen.dart:50-70`). */
    <main className="flex min-h-dvh flex-col items-center gap-10 p-6">
      <div className="flex w-full max-w-[640px] flex-wrap items-center justify-between gap-4">
        <TuteroLogo type="lockup" height={40} />
        {step ? <Steps at={step} /> : null}
      </div>
      <section className="bg-card w-full max-w-[640px] rounded-[18px] border p-10 shadow-sm">
        {body}
      </section>
      {/* Flutter's own escape from a half-finished request (`admin_access_screen.dart:28-42`). */}
      {staff ? (
        <Button variant="outline" onClick={() => void signOut()}>
          Log Out
        </Button>
      ) : null}
    </main>
  );
}

type Step = 'account' | 'verification';

/**
 * Account → Verification, Flutter's two steps (`admin_access_steps_indicator.dart`), as a
 * list so a screen reader is told which of them the page is on rather than being told a
 * circle is blue.
 */
function Steps({ at }: { at: Step }) {
  return (
    <ol aria-label="Asking for access" className="flex items-center gap-2">
      {(['account', 'verification'] as const).map((step, index) => (
        <li
          key={step}
          aria-current={step === at ? 'step' : undefined}
          className="text-regular-s flex items-center gap-2"
        >
          {index === 0 ? null : (
            <span aria-hidden="true" className="bg-border h-px w-6" />
          )}
          <span
            className={`flex size-6 items-center justify-center rounded-full text-[0.75rem] ${
              step === at
                ? 'bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground border'
            }`}
          >
            {index + 1}
          </span>
          <span className={step === at ? undefined : 'text-muted-foreground'}>
            {step === 'account' ? 'Account' : 'Verification'}
          </span>
        </li>
      ))}
    </ol>
  );
}

/* ── Face 1: ask ───────────────────────────────────────────────────────────── */

/**
 * The name, the address and the password (`admin_access_form.dart:100-139`). `type` and
 * `required` are the whole of the validation, as sign-in's are (`staff-sign-in-here` D5):
 * Flutter's validators always return null, so an empty form still fires a sign-up.
 *
 * The name goes whole to the Worker (D5). Flutter sends `split(' ').first` and
 * `split(' ').last`, which makes firstName and lastName identical for a one-word name (F8).
 */
function SignUpFace({ onAsked }: { onAsked: (password: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const refreshSession = useRefreshSession();
  const titleId = useId();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signUpWithEmail({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
      });
    } catch (err) {
      setError(toAuthError(err).message);
      setBusy(false);
      return;
    }
    /* Filed the moment the account exists — before the mail, before any factor — so every
       later step is an enrichment that can fail safely (D3, rule 1; the F3 fix). */
    try {
      await fileAdminAccessRequest();
    } catch {
      /* Not a refused sign-up: the next face says it is not on file, and offers the retry. */
    }
    onAsked(password);
    await client.invalidateQueries({ queryKey: REQUEST_KEY });
    setBusy(false);
    /* It navigates nothing (`staff-sign-in-here` D9): the session read moves the face. */
    refreshSession();
  }

  return (
    <form
      className="flex flex-col gap-4"
      aria-labelledby={titleId}
      onSubmit={(e) => void submit(e)}
    >
      <h1 id={titleId} className="text-headline-4 text-center">
        Request Access to Tutero Admin
      </h1>
      <AuthField
        label="Full Name"
        autoComplete="name"
        placeholder="John Doe"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <AuthField
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="you@tutero.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <AuthField
        label="Password"
        password
        autoComplete="new-password"
        placeholder="6+ Characters"
        minLength={6}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      <AuthErrorText>{error}</AuthErrorText>
      <Button type="submit" disabled={busy} aria-busy={busy}>
        {busy ? <Spinner /> : null}
        Continue
      </Button>
      {/* Flutter's own footer (`admin_access_form.dart:141-159`); `/` is where sign-in is. */}
      <p className="text-regular-s text-center">
        Already have an account?{' '}
        <Link to="/" className="text-primary underline">
          Log in
        </Link>
      </p>
    </form>
  );
}

/* ── Face 2: confirm the address ───────────────────────────────────────────── */

/**
 * Flutter's `confirm_email_widget.dart`, with the two things it has no way to do: a resend,
 * which `sendOnSignUp` plus `resendVerificationEmail` make free (F5, F7), and a way to see
 * the verification land without reloading the tab (F4). Re-reading the account is a press,
 * never a timer: `accountQuery` also re-reads when the window comes back into focus, which
 * is the press most people will not need to make.
 */
function ConfirmEmailFace({ email, filed }: { email: string; filed: boolean }) {
  const client = useQueryClient();
  const resend = useMutation({
    mutationFn: () => resendVerificationEmail(email),
  });

  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Icon name="mail" size={48} className="text-muted-foreground" />
      <h1 className="text-headline-4">Please Confirm Your Email</h1>
      <p className="text-regular-s">We’ve sent an email to {email}.</p>
      <p className="text-regular-s">
        To proceed with your Tutero Admin access request, please verify your
        email by clicking on the link sent to your inbox.
      </p>
      {filed ? null : <NotFiledNotice />}
      <AuthErrorText>
        {resend.isError ? toAuthError(resend.error).message : null}
      </AuthErrorText>
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          type="button"
          onClick={() =>
            void client.invalidateQueries({ queryKey: ACCOUNT_KEY })
          }
        >
          I’ve Verified My Email
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={resend.isPending}
          onClick={() => resend.mutate()}
        >
          {resend.isPending ? 'Sending…' : 'Send the Email Again'}
        </Button>
      </div>
      {resend.isSuccess ? (
        <p role="status" className="text-regular-s">
          We sent another link to {email}.
        </p>
      ) : null}
    </div>
  );
}

/* ── Face 3: prove a number, and make it the second step ───────────────────── */

/**
 * Flutter's `verify_phone_widget.dart`, with one field holding the whole number instead of
 * a country-code picker beside digits — the pair that put two plus signs on every Flutter
 * enrolment (`src/lib/account.ts` › `phoneNumberOf`).
 *
 * Two acts, because the Worker's are two: the texted code proves the number, then the
 * password makes it the second step. Both wrappers are the live ones Settings uses; nothing
 * here is stubbed. A dropped text is another press, not a page reload (F6).
 */
function VerifyPhoneFace({
  password,
  filed,
}: {
  password: string;
  filed: boolean;
}) {
  const client = useQueryClient();
  const [typed, setTyped] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [retyped, setRetyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const titleId = useId();

  /* Held from the form where it can be; asked for again where a reload lost it (D5). */
  const held = password === '' ? retyped : password;
  const number = phoneNumberOf(typed);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (sentTo === null) {
        if (number === null) {
          setError(
            'Enter the number with its country prefix, as +61 412 345 678.',
          );
          return;
        }
        await sendPhoneCode(number);
        setSentTo(number);
        setCode('');
        return;
      }
      await verifyPhoneNumber(sentTo, code.trim());
      /* The Worker refuses `method: 'otp'` until the number is proved, so the order is its. */
      await enrolTextFactor(held);
      await client.invalidateQueries({ queryKey: ACCOUNT_KEY });
      await client.invalidateQueries({ queryKey: REQUEST_KEY });
    } catch (err) {
      setError(toAuthError(err).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-4"
      aria-labelledby={titleId}
      onSubmit={(e) => void submit(e)}
    >
      <h1 id={titleId} className="text-headline-4 text-center">
        Verify Your Phone Number
      </h1>
      {filed ? null : <NotFiledNotice />}
      {sentTo === null ? (
        <AuthField
          label="Phone Number"
          type="tel"
          autoComplete="tel"
          placeholder="+61 412 345 678"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          required
        />
      ) : (
        <>
          <p className="text-regular-s">We texted a code to {sentTo}.</p>
          <AuthField
            label="Verification Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          {password === '' ? (
            <AuthField
              label="Your password"
              password
              autoComplete="current-password"
              value={retyped}
              onChange={(e) => setRetyped(e.target.value)}
              required
            />
          ) : null}
        </>
      )}
      <AuthErrorText>{error}</AuthErrorText>
      <Button type="submit" disabled={busy} aria-busy={busy}>
        {busy ? <Spinner /> : null}
        {sentTo === null ? 'Send Code' : 'Verify'}
      </Button>
      {sentTo === null ? null : (
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            setSentTo(null);
            setCode('');
            setError(null);
          }}
        >
          Use Another Number
        </Button>
      )}
    </form>
  );
}

/* ── Face 4, and the face it is not ────────────────────────────────────────── */

/** Flutter's `request_received_widget.dart`, drawn only when the server says there is one. */
function ReceivedFace() {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <Icon name="check_circle" size={48} className="text-success" />
      <h1 className="text-headline-4">Your Request Has Been Received!</h1>
      <p className="text-regular-s">
        We will review your request and get back to you soon!
      </p>
    </div>
  );
}

/**
 * The outcome Flutter's screen has no face for: `respondToAdminApplication(false)` is
 * reachable from no Flutter UI at all, so a rejected requester there waits on *we will get
 * back to you soon* for good. The server gives a status and no reason, and so does this.
 */
function RejectedFace() {
  return (
    <Refused title="Your request was not approved">
      Tutero has answered your request for admin access, and it was not
      approved. Nothing more on this page will change that. If you think it is a
      mistake, speak to someone at Tutero.
    </Refused>
  );
}

/**
 * Approved while this tab was open. The approval wrote the role in the same batch, so the
 * session is the only thing behind — reading it again draws the shell, or the face above it.
 */
function ApprovedFace() {
  const refreshSession = useRefreshSession();
  return (
    <Refused
      title="Your request was approved"
      action={
        <Button type="button" onClick={refreshSession}>
          Go to Admin
        </Button>
      }
    >
      You have admin access now.
    </Refused>
  );
}

/**
 * Everything is verified and no request is on file: the account and the record are two
 * systems and cannot be one transaction (D3), so this state is reachable — and it says so
 * rather than reporting a success nobody can see. This is the whole of the F3 fix.
 */
function NotFiledFace() {
  const file = useFileRequest();
  return (
    <Refused
      title="We could not file your request"
      action={
        <Button
          type="button"
          disabled={file.isPending}
          onClick={() => file.mutate()}
        >
          {file.isPending ? 'Filing…' : 'File My Request'}
        </Button>
      }
    >
      Your account is made and verified, but no request has reached Tutero.
      Nothing is created twice, so this is safe to try again.
      {file.isError ? ` ${said(file.error)}` : ''}
    </Refused>
  );
}

/** The same truth said in passing, while the flow still has steps left to run. */
function NotFiledNotice() {
  const file = useFileRequest();
  return (
    <div
      role="alert"
      className="border-destructive/40 bg-destructive/5 flex flex-wrap items-center gap-2 rounded-md border p-3 text-left"
    >
      <Icon name="warning" size={16} className="text-destructive" />
      <span className="text-bold-s text-destructive">
        Your request is not on file yet
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-auto"
        disabled={file.isPending}
        onClick={() => file.mutate()}
      >
        {file.isPending ? 'Filing…' : 'File It Now'}
      </Button>
      {file.isError ? (
        <p className="text-destructive text-regular-s basis-full">
          {said(file.error)}
        </p>
      ) : null}
    </div>
  );
}

/** A staff member who followed the link anyway. They have the thing the page asks for. */
function AlreadyStaffFace({ email }: { email: string }) {
  return (
    <Refused
      title="You already have admin access"
      action={
        <Link to="/" className="text-primary text-regular-m underline">
          Back to Admin
        </Link>
      }
    >
      You are signed in as {email}, which is already a staff account.
    </Refused>
  );
}

/* ── The parts the faces share ─────────────────────────────────────────────── */

/** File the request, then let the self-read decide what that changed (D3, D4). */
function useFileRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fileAdminAccessRequest,
    onSuccess: () => client.invalidateQueries({ queryKey: REQUEST_KEY }),
  });
}

function Retry({ onPress }: { onPress: () => void }) {
  return (
    <Button type="button" variant="outline" onClick={onPress}>
      Retry
    </Button>
  );
}

function Refused({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <h1 className="text-headline-4">{title}</h1>
      <p className="text-regular-s">{children}</p>
      {action}
    </div>
  );
}
